import { useState, useRef, useEffect, useCallback } from 'react';

const OUTPUT_SIZE = 256;
const VIEW_SIZE = 300; // viewport diameter in CSS pixels
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

/**
 * LinkedIn / Instagram-style circular profile picture cropper.
 *
 * Props:
 *   file       – File object from <input type="file">
 *   onCrop     – (blob: Blob) => void  — called with the final 256×256 JPEG blob
 *   onCancel   – () => void
 */
export default function ProfilePicCropper({ file, onCrop, onCancel }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);

  // Load selected file into an Image
  useEffect(() => {
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setImgLoaded(true);
    };
    img.src = URL.createObjectURL(file);
    return () => URL.revokeObjectURL(img.src);
  }, [file]);

  // Compute the base scale so the smaller dimension fills the viewport
  const baseScale = useCallback(() => {
    const img = imgRef.current;
    if (!img) return 1;
    return VIEW_SIZE / Math.min(img.width, img.height);
  }, []);

  // Draw the image on the visible canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = VIEW_SIZE * dpr;
    canvas.height = VIEW_SIZE * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, VIEW_SIZE, VIEW_SIZE);

    const s = baseScale() * zoom;
    const dw = img.width * s;
    const dh = img.height * s;
    const dx = (VIEW_SIZE - dw) / 2 + offset.x;
    const dy = (VIEW_SIZE - dh) / 2 + offset.y;

    ctx.drawImage(img, dx, dy, dw, dh);

    // Darken area outside the circle
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.beginPath();
    ctx.rect(0, 0, VIEW_SIZE, VIEW_SIZE);
    ctx.arc(VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_SIZE / 2, 0, Math.PI * 2, true);
    ctx.fill('evenodd');

    // Circle border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
  }, [zoom, offset, imgLoaded, baseScale]);

  // ── Pointer handlers (mouse + touch) ────────────────────────────────────
  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = useCallback(
    (e) => {
      if (!dragging) return;
      setOffset({
        x: dragStart.current.ox + (e.clientX - dragStart.current.x),
        y: dragStart.current.oy + (e.clientY - dragStart.current.y),
      });
    },
    [dragging],
  );
  const onPointerUp = useCallback(() => setDragging(false), []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      return () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };
    }
  }, [dragging, onPointerMove, onPointerUp]);

  // ── Crop: render final 256×256 circle into an offscreen canvas ──────────
  const handleCrop = () => {
    const img = imgRef.current;
    if (!img) return;

    const out = document.createElement('canvas');
    out.width = OUTPUT_SIZE;
    out.height = OUTPUT_SIZE;
    const ctx = out.getContext('2d');

    // Clip to circle
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // Map the viewport crop to the output size
    const ratio = OUTPUT_SIZE / VIEW_SIZE;
    const s = baseScale() * zoom;
    const dw = img.width * s * ratio;
    const dh = img.height * s * ratio;
    const dx = ((VIEW_SIZE - img.width * s) / 2 + offset.x) * ratio;
    const dy = ((VIEW_SIZE - img.height * s) / 2 + offset.y) * ratio;

    ctx.drawImage(img, dx, dy, dw, dh);

    out.toBlob(
      (blob) => {
        if (blob) onCrop(blob);
      },
      'image/jpeg',
      0.92,
    );
  };

  if (!file) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          maxWidth: 380,
          width: '90vw',
          textAlign: 'center',
        }}
      >
        <h5 className="mb-3 fw-bold">Crop Profile Picture</h5>

        {/* Canvas viewport */}
        <div
          style={{
            width: VIEW_SIZE,
            height: VIEW_SIZE,
            margin: '0 auto',
            borderRadius: '50%',
            overflow: 'hidden',
            cursor: dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{ width: VIEW_SIZE, height: VIEW_SIZE, display: 'block' }}
            onPointerDown={onPointerDown}
          />
        </div>

        {/* Zoom slider */}
        <div className="d-flex align-items-center gap-2 mt-3 px-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
          <input
            type="range"
            className="form-range flex-grow-1"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </div>

        {/* Actions */}
        <div className="d-flex gap-2 mt-3 justify-content-center">
          <button className="btn btn-outline-secondary px-4" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-primary px-4" onClick={handleCrop}>
            Crop &amp; Save
          </button>
        </div>
      </div>
    </div>
  );
}
